import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import api from 'api';

import Button from 'components/partials/button/button';
import Loader from 'components/partials/loader/loader';
import ReportForm, {
  type ReportFormValues,
  type PowerBiReport,
  type ProductChoice,
} from 'containers/admin/reports/report/report-form';
import toast from 'components/partials/toast/toast';
import Heading from 'components/partials/heading/heading';
import ResponsiveWrapper from 'components/shared/others/responsive-wrapper/responsive-wrapper';
import ActionMenu from 'components/partials/action-menu/action-menu';
import Breadcrumbs from 'components/partials/beadcrumbs/breadcrumbs';
import { getActionMenuItems } from 'containers/admin/reports/report/report.helpers';

import { useCurrentUser } from 'state/current-user/current-user.thunk';
import {
  useCreateReport,
  useModifyReport,
  useDeleteReport,
} from 'hooks/api/use-admin-reports';

interface AdminReportProps {
  reportId: string;
}

interface ReportRecord extends ReportFormValues {
  powerBiTitle?: string;
}

const AdminReport = ({ reportId }: AdminReportProps): React.JSX.Element => {
  const user = useCurrentUser();
  const userId = (user?.data?.oktaUserId ?? '') as string;

  const navigate = useNavigate();
  const isNew = reportId === 'new';

  const createReportMutation = useCreateReport();
  const modifyReportMutation = useModifyReport();
  const deleteReportMutation = useDeleteReport();

  const [reportData, setReportData] = useState<ReportRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [disabled, setDisabled] = useState(!isNew);
  const [powerBiReports, setPowerBiReports] = useState<PowerBiReport[]>([]);
  const [products, setProducts] = useState<ProductChoice[]>([]);

  const { control, handleSubmit, reset, setFocus, clearErrors } =
    useForm<ReportFormValues>({
      defaultValues: {
        title: '',
        description: '',
        powerBiId: '',
        productId: '',
      },
    });

  useEffect(() => {
    setFocus('title');
  }, [disabled]);

  useEffect(() => {
    const fetchPowerBiReports = async (): Promise<void> => {
      try {
        const response = await api.getPowerBiReports({
          userId,
        });

        if (response?.data) {
          setPowerBiReports(response.data);
        }
      } catch (error) {
        console.error('Error while fetching power bi reports', error);
      }
    };

    fetchPowerBiReports();
  }, []);

  useEffect(() => {
    const fetchProducts = async (): Promise<void> => {
      try {
        const response = await api.getProducts({
          userId,
        });

        if (response?.data) {
          setProducts(response.data);
        }
      } catch (error) {
        console.error('Error while fetching power bi reports', error);
      }
    };

    fetchProducts();
  }, []);

  useEffect(() => {
    if (!isNew) {
      const fetchReport = async (): Promise<void> => {
        setLoading(true);
        try {
          const response = await api.getReport({
            userId,
            reportId,
          });

          if (!response?.data) {
            return;
          }

          const { title, description, powerBiId, productId, powerBiTitle } =
            response.data;

          setReportData({
            title: title ?? '',
            description: description ?? '',
            powerBiId: String(powerBiId ?? ''),
            productId: String(productId ?? ''),
            powerBiTitle,
          });
          reset({
            title: title ?? '',
            description: description ?? '',
            powerBiId: String(powerBiId ?? ''),
            productId: String(productId ?? ''),
          });
        } catch (error) {
          console.error('Error while fetching report data', error);
        } finally {
          setLoading(false);
        }
      };

      fetchReport();
    } else {
      setLoading(false);
    }
  }, [reportId, isNew, reset]);

  const onSubmit = async (data: ReportFormValues): Promise<void> => {
    setLoading(true);

    const transformedData = { ...data, productId: data.productId || undefined };

    try {
      if (isNew) {
        await createReportMutation.mutateAsync({
          userId,
          reportData: transformedData,
        });
      } else {
        await modifyReportMutation.mutateAsync({
          userId,
          reportId,
          reportData: transformedData,
        });
      }
      toast.success({
        title: `The report was successfully ${isNew ? 'created' : 'updated'}`,
      });
      navigate('/admin/reports');
    } catch (error) {
      toast.error({ title: 'Something went wrong' });
      console.error('Error while saving report', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    setLoading(true);
    try {
      await deleteReportMutation.mutateAsync({ userId, reportId });
      toast.success({
        title: `The report was successfully removed`,
      });
      navigate('/admin/reports');
    } catch (error) {
      toast.error({ title: 'Something went wrong' });
      console.error('Error while report deleting', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = (): void => {
    if (isNew) {
      navigate('/admin/reports');
    } else {
      setDisabled(true);
      reset(reportData ?? undefined);
    }
  };

  const reportTitle = isNew ? 'New Report' : reportData?.title;

  return (
    <ResponsiveWrapper customClass="my-4">
      <Breadcrumbs
        breadcrumbs={[
          { label: 'Reports', href: '/admin/reports' },
          { label: reportTitle ?? '', isActive: true },
        ]}
      />
      <div className="flex items-center justify-between mt-3">
        <Heading variant="h1">{reportTitle}</Heading>

        {!isNew && (
          <ActionMenu
            buttonName="Edit report"
            options={getActionMenuItems({
              handleDelete: () => {
                void handleDelete();
              },
              reportId,
              reportName: reportData?.title,
            })}
            onEditClick={() => setDisabled(false)}
            isActionMenuDisabled={!disabled}
          />
        )}
      </div>

      <Loader loading={loading} fullScreen={false} />

      <form onSubmit={handleSubmit(onSubmit)}>
        <ReportForm
          control={control}
          disabled={disabled}
          powerBiReports={powerBiReports}
          products={products}
          clearErrors={clearErrors}
          powerBiData={{
            value: reportData?.powerBiId,
            label: reportData?.powerBiTitle,
          }}
        />

        {(!disabled || isNew) && (
          <div className="mt-4 flex justify-end">
            <Button
              type="button"
              variant="reserve-text"
              onClick={handleCancel}
              data-testid="cancel-report-button"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="brand-filled"
              data-testid="save-report-button"
            >
              {isNew ? 'Add Report' : 'Save Changes'}
            </Button>
          </div>
        )}
      </form>
    </ResponsiveWrapper>
  );
};

export default AdminReport;
